import { useCallback, useMemo } from "react";

import { FormInstance } from "antd";
import { useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";

import {
    // selectors
    selectFormFields,
    // actions
    setFields,
} from "Services/state/journeys/JourneyStateSlice";
import { useRVDispatch } from "Services/state/Storage";

type UseDynamicPanelsProps<T> = {
    form: FormInstance;
    generatePanel: any;
    listName: string;
    itemName: string;
    itemComponent: React.ComponentType<T>;
    itemComponentProps: T;
};

export function useUpdateContactPanels<T>({
    form,
    generatePanel,
    listName,
    itemName,
    itemComponent,
    itemComponentProps,
}: UseDynamicPanelsProps<T>) {
    const dispatch = useRVDispatch();
    const fields = useSelector(selectFormFields);

    // ALL OF THIS SHOULD BE REDUCER CODE BUT THE REDUCERS
    // BUILT TO OVERWRITE THE WHOLE STATE....
    const addFormPanel = useCallback(() => {
        const newId = uuidv4();
        if (fields && fields.length && typeof fields[0].name !== "string") {
            const propertyFieldName = [listName, newId, itemName];
            dispatch(setFields([...fields, { name: propertyFieldName, value: "" }]));
        } else {
            const propIndex = fields.findIndex((field: any) => {
                return field.name.includes(listName);
            });

            if (propIndex === -1) {
                const newField = {
                    name: listName,
                    value: {
                        [newId]: {
                            [itemName]: "",
                        },
                    },
                };
                const newFields = [...fields, newField];
                dispatch(setFields(newFields));
            } else {
                const newField = {
                    ...fields[propIndex],
                    value: {
                        ...fields[propIndex].value,
                        [newId]: {
                            [itemName]: "",
                        },
                    },
                };
                const newFields = [...fields.slice(0, propIndex), newField, ...fields.slice(propIndex + 1)];
                dispatch(setFields(newFields));
            }
        }
    }, [dispatch, fields, listName, itemName]);

    // ALL OF THIS SHOULD BE REDUCER CODE BUT THE REDUCERS
    // BUILT TO OVERWRITE THE WHOLE STATE....
    const removeFormPanel = useCallback(
        (id: string) => {
            const updatedFields = Array.from(fields);
            if (typeof fields[0].name !== "string") {
                const filteredFields = fields.filter((field: any) => {
                    return field.name[1] !== id;
                });
                dispatch(setFields(filteredFields));
            } else {
                const propIndex = fields.findIndex((field: any) => {
                    return field.name.includes(listName);
                });

                const newField = {
                    ...updatedFields[propIndex],
                    value: { ...updatedFields[propIndex].value },
                };

                delete newField.value[id];

                updatedFields[propIndex] = newField;
                dispatch(setFields(updatedFields));
            }
        },
        [dispatch, fields, listName]
    );

    const panelsList = useMemo(() => {
        let keys: Array<string> = [];
        if (!fields || fields.length === 0) {
            return [];
        }

        if (typeof fields[0].name != "string") {
            keys = fields
                .filter((field: any) => {
                    return field.name[0] === listName && field.name[2] === itemName;
                })
                .map((field: any) => {
                    return field.name[1];
                });
        } else {
            keys = fields
                .filter((field: any) => {
                    return field?.name?.includes(listName);
                })
                .map((field: any) => {
                    return Object.keys(field.value);
                })[0];
        }

        return keys
            ? keys.map((key: string, index: number) => {
                  // @ts-expect-error - complex typing
                  return generatePanel<T>({
                      form,
                      index,
                      remove: () => {
                          removeFormPanel(key);
                      },
                      id: key,
                      contentComponent: itemComponent,
                      contentProps: itemComponentProps,
                  });
              })
            : [];
    }, [fields, listName, itemName, generatePanel, form, itemComponent, itemComponentProps, removeFormPanel]);

    return {
        addFormPanel,
        panelsList,
    };
}
